import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { InView, EyebrowLabel, InlineCta } from "../utils/Animation";
import NotFound from "../pages/404";
import {
  SERVICE_DATA_MAP,
  type ServiceData,
} from "../data/services/servicesRegistry";

export default function SingleService() {
  const { slug } = useParams<{ slug: string }>();
  const service: ServiceData | undefined = slug
    ? SERVICE_DATA_MAP[slug]
    : undefined;

  if (!service) {
    return (
      <NotFound
        heading="Service not found."
        message="This service page may have moved or been updated."
        backLabel="Back to services"
        backHref="/services"
      />
    );
  }

  return (
    <>
      {/* ── Hero ── */}
      <div className="relative bg-itl-navy pt-40 pb-32 overflow-hidden">
        <img
          src={service.heroImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-itl-navy/60 to-itl-navy" />
        <div className="absolute left-6 lg:left-12 top-0 bottom-0 flex items-center pointer-events-none">
          <div className="h-[35%] w-px bg-white/10" />
        </div>

        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-12">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="flex items-center gap-2 mb-8"
          >
            <Link
              to="/services"
              className="text-[11px] uppercase tracking-widest text-white/40 hover:text-white/70 transition-colors"
            >
              Services
            </Link>
            <span className="text-white/20 text-xs">/</span>
            <span className="text-[11px] uppercase tracking-widest text-itl-blue">
              {service.title}
            </span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-[11px] font-semibold uppercase tracking-[0.25em] text-itl-blue mb-4"
          >
            {service.tag}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.85,
              ease: [0.22, 1, 0.36, 1],
              delay: 0.3,
            }}
            className="font-serif text-5xl lg:text-7xl font-light text-white max-w-3xl leading-[1.06] mb-6"
          >
            {service.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-white/55 text-lg max-w-xl leading-relaxed"
          >
            {service.tagline}
          </motion.p>
        </div>
      </div>

      {/* ── Overview ── */}
      <section className="bg-white py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-16">
          <InView>
            <EyebrowLabel>Overview</EyebrowLabel>
            <h2 className="font-serif text-3xl font-light text-itl-navy leading-snug">
              What this service covers, and why it matters.
            </h2>
          </InView>
          <InView delay={0.1}>
            <div className="flex flex-col gap-5">
              {service.overview.map((para, i) => (
                <p
                  key={i}
                  className="text-gray-600 leading-relaxed text-[15px]"
                >
                  {para}
                </p>
              ))}
            </div>
          </InView>
        </div>
      </section>

      {/* ── Deliverables + Tech Stack ── */}
      <section className="bg-[#f7f7f5] py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <InView>
            <EyebrowLabel>What We Deliver</EyebrowLabel>
            <h2 className="font-serif text-3xl font-light text-itl-navy mb-8 leading-snug">
              Tangible outputs, not vague promises.
            </h2>
            <ul className="flex flex-col gap-4">
              {service.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 bg-itl-blue rounded-full shrink-0" />
                  <span className="text-gray-600 text-[14px] leading-snug">
                    {d}
                  </span>
                </li>
              ))}
            </ul>
          </InView>

          <InView delay={0.1}>
            <EyebrowLabel>Technology Stack</EyebrowLabel>
            <h2 className="font-serif text-3xl font-light text-itl-navy mb-8 leading-snug">
              Battle-tested tools, chosen for the right reasons.
            </h2>
            <div className="flex flex-col divide-y divide-gray-200">
              {service.techStack.map((t) => (
                <div
                  key={t.name}
                  className="py-4 flex items-start justify-between gap-4"
                >
                  <span className="text-[12px] font-semibold uppercase tracking-widest text-itl-navy">
                    {t.name}
                  </span>
                  <span className="text-gray-500 text-[13px] text-right">
                    {t.description}
                  </span>
                </div>
              ))}
            </div>
          </InView>
        </div>
      </section>

      {/* ── Process ── */}
      <section className="bg-white py-24">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <InView className="mb-14">
            <EyebrowLabel>Our Approach</EyebrowLabel>
            <h2 className="font-serif text-4xl font-light text-itl-navy max-w-xl leading-snug">
              How we deliver this service, step by step.
            </h2>
          </InView>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-100">
            {service.process.map((step, i) => (
              <InView
                key={step.title}
                delay={i * 0.07}
                className="bg-white p-10"
              >
                <span className="font-mono text-[11px] text-gray-300 block mb-4">
                  0{i + 1}
                </span>
                <h3 className="font-serif text-2xl font-light text-itl-navy mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-500 text-[14px] leading-relaxed">
                  {step.body}
                </p>
              </InView>
            ))}
          </div>
        </div>
      </section>

      {/* ── Case study ── */}
      {service.caseStudy && (
        <section className="bg-[#f7f7f5] py-24">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
            <InView className="mb-10">
              <EyebrowLabel>See It In Action</EyebrowLabel>
            </InView>
            <InView delay={0.05}>
              <Link
                to={service.caseStudy.href}
                className="group grid grid-cols-1 lg:grid-cols-2 bg-white border border-gray-100 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="relative overflow-hidden aspect-video lg:aspect-auto">
                  <img
                    src={service.caseStudy.image}
                    alt={service.caseStudy.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-10 lg:p-14 flex flex-col justify-center">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-itl-blue mb-4">
                    Case Study
                  </p>
                  <h3 className="font-serif text-3xl font-light text-itl-navy mb-4 group-hover:text-itl-blue transition-colors leading-snug">
                    {service.caseStudy.title}
                  </h3>
                  <p className="text-gray-500 leading-relaxed mb-6">
                    {service.caseStudy.excerpt}
                  </p>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-itl-blue flex items-center gap-2">
                    View project{" "}
                    <span className="group-hover:translate-x-1 transition-transform inline-block">
                      →
                    </span>
                  </p>
                </div>
              </Link>
            </InView>
          </div>
        </section>
      )}

      {/* ── Related services ── */}
      <section className="bg-white py-16">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
          <InView className="mb-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
              Related Services
            </p>
          </InView>
          <div className="flex flex-wrap gap-3">
            {service.relatedServices.map((r) => (
              <Link
                key={r.href}
                to={r.href}
                className="px-5 py-3 border border-gray-200 text-[12px] font-semibold uppercase tracking-widest text-itl-navy hover:bg-itl-navy hover:text-white hover:border-itl-navy transition-colors duration-200"
              >
                {r.title} →
              </Link>
            ))}
          </div>
        </div>
      </section>

      <InlineCta
        heading="Ready to get started with this service?"
        body="Tell us about your project and we'll respond within 24 hours with a clear proposal."
        ctaLabel="Request a Proposal"
        ctaHref="/contact"
      />
    </>
  );
}
